/* global Office */
/* global Word */
import {
  OptionOnSelectData,
  SelectionEvents,
  Skeleton,
  SkeletonItem,
  TextareaOnChangeData,
} from "@fluentui/react-components";
import React, { useCallback, useEffect } from "react";
import { useChatStyles } from "./styles";
import { useDocumentClassification } from "@/context/document-classification";
import { getDocumentText, getSelectedText } from "@/taskpane/lib";
import { queryDocument } from "@/services/chat-service";
import marked from "marked";
import DOMPurify from "dompurify";
import ChatInputText from "@/components/chat-input-text/chat-input-text";
import { useQuestions } from "@/context/questions";
import log from "../../utils/logger";
import { XMLParser } from "fast-xml-parser";
import OfficeSettings from "@/constants/office";
import { OfficeEnvironment } from "@/types/document";
import { flattenObject } from "es-toolkit";
import { useAbortController } from "@/hooks/use-abort-controller";

export const Ask = () => {
  const styles = useChatStyles();
  const { selectedPromptConfig } = useDocumentClassification();
  const { createController, abort, cleanup, getSignal } = useAbortController();
  const {
    query,
    setQuery,
    selectedDocumentExcerpt,
    setSelectedDocumentExcerpt,
    documentExcerptOption,
    setDocumentExcerptOption,
    selectedTextError,
    setSelectedTextError,
    loadingQuery,
    setLoadingQuery,
    queryReply,
    setQueryReply,
  } = useQuestions();

  const onTextSelectionChange = useCallback(async () => {
    const { text: selectedText } = await getSelectedText();
    setSelectedTextError(selectedText || documentExcerptOption === "Full Document" ? "" : "Please select some text");
  }, [setSelectedTextError, documentExcerptOption]);

  useEffect(() => {
    Office.onReady(() => {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        onTextSelectionChange,
        onTextSelectionChange
      );
    });

    return () => {
      Office.onReady(() => {
        Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, onTextSelectionChange);
      });
    };
  }, [onTextSelectionChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abort();
      setLoadingQuery(false);
      cleanup();
    };
  }, []);

  const deletedChangesFromXml = async (changes: Word.TrackedChangeCollection, context: Word.RequestContext) => {
    let deletedXmlStrings = changes.items.filter(({ type }) => type == "Deleted").map((c) => c.getRange().getOoxml());
    await context.sync();
    let deletedXml = deletedXmlStrings.map((deleted) => new XMLParser().parse(deleted.value));
    const deleted = deletedXml.map((xml) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, text] = Object.entries(flattenObject(xml)).find(([key, _]) => key.includes("w:delText")) ?? "";

      return { type: "Deleted", text };
    });
    return deleted;
  };

  const getDocumentChanges = async () => {
    await Office.onReady();

    return await Word.run(async (context) => {
      const body: Word.Body = context.document.body;
      const changes: Word.TrackedChangeCollection = body.getTrackedChanges();
      changes.load("items");
      await context.sync();
      const deletedChanges =
        OfficeSettings.environment === OfficeEnvironment.Desktop ? await deletedChangesFromXml(changes, context) : [];

      const documentChanges: { text: string; type: string }[] = changes.items
        .filter(({ text }) => text.length > 0)
        .map(({ type, text }) => ({ type, text }))
        //@ts-ignore
        .concat(deletedChanges);

      return ["```json", JSON.stringify(documentChanges), "```"].join("\n");
    });
  };

  const onQueryDocument = async () => {
    try {
      setQueryReply("");
      if (!query || !selectedDocumentExcerpt.length || !documentExcerptOption) return;
      createController();
      setLoadingQuery(true);
      let documentExcerpt: string;
      const wholeDocument = await getDocumentText();
      if (documentExcerptOption === "Full Document") {
        documentExcerpt = await getDocumentText();
      } else {
        documentExcerpt = (await getSelectedText()).text;
        if (!documentExcerpt) {
          setSelectedTextError("Please select some text.");
          setLoadingQuery(false);
          return;
        } else {
          setSelectedTextError("");
        }
      }
      const changes = await getDocumentChanges();
      const reply = await queryDocument({
        documentExcerpt,
        wholeDocument: wholeDocument + changes,
        promptConfigId: selectedPromptConfig.id,
        query,
        abortSignal: getSignal(),
      });
      setQueryReply(reply);
    } catch (e) {
      if (e.message === "Request was cancelled") {
        log.debug("Request was cancelled by user", "onQueryDocument", e);
        setQueryReply("");
      } else {
        log.error("Ask page error", "onQueryDocument", e);
      }
    } finally {
      setLoadingQuery(false);
      cleanup();
    }
  };

  const onStopQuery = () => {
    abort();
  };

  const onQueryChange = (_ev: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => {
    setQuery(data.value);
  };

  const onDocumentExcerptChange = (_event: SelectionEvents, data: OptionOnSelectData) => {
    setSelectedDocumentExcerpt(data.selectedOptions);
    setDocumentExcerptOption(data.optionText);
  };

  return (
    <>
      <ChatInputText
        query={query}
        onQueryChange={onQueryChange}
        dropdown={{ documentExcerptOption, onDocumentExcerptChange, selectedDocumentExcerpt }}
        sendBtn={{
          onClick: onQueryDocument,
          disabled:
            !query || !selectedDocumentExcerpt.length || !documentExcerptOption || !!selectedTextError || loadingQuery,
        }}
        stopBtn={{
          onClick: onStopQuery,
          disabled: !loadingQuery,
        }}
        loading={loadingQuery}
        error={selectedTextError}
      />
      {loadingQuery && (
        <Skeleton className={styles.skeleton}>
          {Array.from({ length: 10 }, (_v, i) => i).map((i) => (
            <SkeletonItem key={`skeleton-${i}`} />
          ))}
        </Skeleton>
      )}
      {!loadingQuery && !!queryReply && (
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(marked.parse(queryReply, { async: false, breaks: true, gfm: true })),
          }}
        />
      )}
    </>
  );
};
